import React, { useState, useRef } from 'react';
import { Upload, message, Progress } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd/es/upload/interface';
import mediaService, { MediaFile } from '../../services/media';
import { getAxiosErrorMessage } from '../../utils/error';

const { Dragger } = Upload;

interface MediaUploaderProps {
  onUploadSuccess?: (mediaFile: MediaFile) => void;
  onUploadError?: (error: any) => void;
  multiple?: boolean;
  accept?: string;
  maxSize?: number; // in MB
  showProgress?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

interface UploadingFile extends UploadFile {
  progress?: number;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  onUploadSuccess,
  onUploadError,
  multiple = false,
  accept,
  maxSize = 50,
  showProgress = true,
  style,
  className,
}) => {
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadingFile[]>([]);
  const processedFiles = useRef<Set<string>>(new Set());
  const isUploading = useRef<boolean>(false);

  const uploadProps: UploadProps = {
    name: 'file',
    multiple,
    accept,
    fileList,
    beforeUpload: (file) => {
      // 检查文件大小
      const isValidSize = file.size / 1024 / 1024 < maxSize;
      if (!isValidSize) {
        message.error(`文件大小不能超过 ${maxSize}MB`);
        return false;
      }

      // 如果不支持多选且已有文件，替换原文件
      if (!multiple && fileList.length > 0) {
        setFileList([]);
      }

      return false; // 阻止自动上传
    },
    onChange: (info) => {
      const { fileList } = info;
      setFileList(fileList);
      
      // 找出未处理的新文件
      const filesToUpload = fileList.filter(file => 
        file.status === undefined && 
        !processedFiles.current.has(file.uid) &&
        file.originFileObj
      );
      
      if (filesToUpload.length > 0 && !isUploading.current) {
        // 立即标记为已处理，防止重复
        filesToUpload.forEach(file => {
          processedFiles.current.add(file.uid);
        });
        
        handleAutoUpload(filesToUpload);
      }
    },
    onRemove: (file) => {
      setFileList(prev => prev.filter(item => item.uid !== file.uid));
    },
  };

  const handleAutoUpload = async (files: UploadingFile[]) => {
    if (files.length === 0 || isUploading.current) {
      return;
    }

    isUploading.current = true;
    setUploading(true);

    try {
      const uploadPromises = files.map(async (uploadFile) => {
        if (uploadFile.originFileObj) {
          const formData = {
            file: uploadFile.originFileObj,
            title: uploadFile.name, // 使用原始文件名作为标题
            alt_text: '',
            description: '',
          };

          try {
            const response = await mediaService.uploadMedia(formData);
            if (response.success && response.data) {
              return response.data;
            } else {
              throw new Error(response.message || '上传失败');
            }
          } catch (error: any) {
            console.error('Upload error:', error);
            throw new Error(getAxiosErrorMessage(error, '上传失败'));
          }
        }
        return null;
      });

      const results = await Promise.allSettled(uploadPromises);
      const successfulUploads = results
        .filter((result): result is PromiseFulfilledResult<MediaFile> => 
          result.status === 'fulfilled' && result.value !== null
        )
        .map(result => result.value);

      const failedUploads = results.filter(result => result.status === 'rejected');

      if (successfulUploads.length > 0) {
        message.success(`成功上传 ${successfulUploads.length} 个文件`);
        successfulUploads.forEach(mediaFile => {
          onUploadSuccess?.(mediaFile);
        });
        
        // 清空文件列表和上传跟踪状态
        setFileList([]);
        processedFiles.current.clear();
      }

      if (failedUploads.length > 0) {
        failedUploads.forEach((result) => {
          if (result.status === 'rejected') {
            console.error('Upload failed:', result.reason);
            onUploadError?.(result.reason);
          }
        });
        message.error(`${failedUploads.length} 个文件上传失败`);
      }

    } catch (error: any) {
      console.error('Upload error:', error);
      message.error(error.message || '上传失败');
      onUploadError?.(error);
    } finally {
      isUploading.current = false;
      setUploading(false);
    }
  };

  return (
    <div className={className} style={style}>
      <Dragger {...uploadProps} disabled={uploading}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {uploading ? '上传中...' : '点击或拖拽文件到此区域进行上传'}
        </p>
        <p className="ant-upload-hint">
          选择文件后将自动开始上传，支持单个或批量上传
          <br />
          文件大小限制为 {maxSize}MB
          {accept && (
            <>
              <br />
              支持的文件类型：{accept}
            </>
          )}
        </p>
      </Dragger>

      {showProgress && uploading && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={100} status="active" />
          <p style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
            正在上传文件，请稍候...
          </p>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
